import { MNSproCloudApi } from '../..';
import {
  ClassbookEntryDefinition,
  ClassbookEntryDefinitionCategory,
} from '../classbook/classbookSettings.interfaces';

export class ClassbookEntryDefinitionService {
  static async getAllClassbookEntryDefinition(
    authToken: string,
    organizationId: string,
  ): Promise<ClassbookEntryDefinition[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllEntryDefinitions(organizationId);

    return result.data as ClassbookEntryDefinition[];
  }

  static async createClassbookEntryDefinition(
    authToken: string,
    apiRequest: {
      classbookEntry: ClassbookEntryDefinition;
      organizationId: string;
    },
  ): Promise<ClassbookEntryDefinition> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.createClassbookEntriesDefinition(
      apiRequest.organizationId,
      {
        ...apiRequest.classbookEntry,
        isDisabled: apiRequest.classbookEntry.isDisabled ?? false,
        category: { id: apiRequest.classbookEntry.category?.id ?? '' },
      },
    );
    return result.data as ClassbookEntryDefinition;
  }

  static async updateClassbookEntryDefinition(
    authToken: string,
    apiRequest: {
      classbookEntry: ClassbookEntryDefinition;
      organizationId: string;
    },
  ): Promise<ClassbookEntryDefinition> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.updateClassbookEntriesDefinition(
      apiRequest.organizationId,
      {
        ...apiRequest.classbookEntry,
        isDisabled: apiRequest.classbookEntry.isDisabled ?? false,
        category: { id: apiRequest.classbookEntry.category?.id ?? '' },
      },
    );
    return result.data as ClassbookEntryDefinition;
  }

  static async deleteClassbookEntryDefinition(
    authToken: string,
    organizationId: string,
    classbookEntryId: string,
  ): Promise<void> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    await api.deleteClassbookEntriesDefinition(
      classbookEntryId,
      organizationId,
    );
  }

  static async getAllCategories(
    authToken: string,
    organizationId: string,
  ): Promise<ClassbookEntryDefinitionCategory[]> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.getAllClassbookEntriesCategories(organizationId);
    const categories = result.data.map(r => ({
      id: r.id || '',
      shortName: r.shortName || '',
      fullName: r.fullName || '',
      description: r.description || '',
      isDisabled: r.isDisabled || false,
    })) as ClassbookEntryDefinitionCategory[];

    return categories;
  }

  static async createCategory(
    authToken: string,
    organizationId: string,
    shortName: string,
    fullName: string,
    description: string,
    isDisabled = false,
  ): Promise<ClassbookEntryDefinitionCategory> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const category: ClassbookEntryDefinitionCategory = {
      description,
      fullName,
      shortName,
      isDisabled,
    };

    const result = await api.createClassbookEntriesCategory(organizationId, {
      ...category,
      isDisabled: category.isDisabled ?? false,
    });

    return result.data as ClassbookEntryDefinitionCategory;
  }

  static async updateCategory(
    authToken: string,
    organizationId: string,
    category: ClassbookEntryDefinitionCategory,
  ): Promise<ClassbookEntryDefinitionCategory> {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    const result = await api.updateClassbookEntriesCategory(organizationId, {
      ...category,
      id: category.id ?? '',
      isDisabled: category.isDisabled ?? false,
    });

    return result.data as ClassbookEntryDefinitionCategory;
  }

  static async deleteCategory(
    authToken: string,
    organizationId: string,
    categoryId: string,
  ) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.ClassRegisterApiFactory(config);

    await api.deleteClassbookEntriesCategory(categoryId, organizationId);
  }
}
